import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import t from 'ember-intl/helpers/t';
import add from 'ember-math-helpers/helpers/add';
import { eq, lt, or } from 'ember-truth-helpers';
import CircleCharacter from 'invite-sign/components/circle-character';
import Substep from 'invite-sign/components/invite-steps/substep';
import type { Step } from 'invite-sign/services/invite-steps';

interface InviteStepsStepSignature {
  Args: {
    currentStep: number;
    index: number;
    step: Step;
  };
}

export default class InviteStepsStep extends Component<InviteStepsStepSignature> {
  @service declare router: RouterService;

  get isActive(): boolean {
    const { currentRouteName } = this.router;
    const { step } = this.args;

    return step.route == currentRouteName || !!step.substeps?.find((substep) => substep.route === currentRouteName);
  }

  get isDone(): boolean {
    return this.args.index < this.args.currentStep;
  }

  get currentSubstep(): number {
    const { currentRoute, currentRouteName: routeName } = this.router;
    if (!currentRoute || !this.isActive) {
      return -1;
    }

    const routeParams = currentRoute.params ?? {};

    const substep = (this.args.step.substeps || []).findIndex(({ id, route }) => {
      if (route !== routeName) {
        return false;
      }
      return (
        id ===
        (routeParams['agreement_id'] ||
          routeParams['document_id'] ||
          routeParams['questionnaire_id'] ||
          routeParams['requirement_id'])
      );
    });
    if (substep !== -1) {
      return substep;
    }
    return Number.MAX_SAFE_INTEGER;
  }

  <template>
    <li data-test-step-label={{@step.label}} data-test-active-step={{this.isActive}}>
      <div
        class="flex items-center mb-1
          {{if this.isDone 'line-through'}}
          {{if this.isActive 'font-extrabold' 'text-grey'}}"
      >
        {{#if this.isDone}}
          <img src="/images/check@3x.png" alt={{t "steps.complete"}} style="width:18px;height:18px;" />
        {{else}}
          <CircleCharacter @number={{add 1 @index}} @fillColor={{if this.isActive "#282c36" "#b8c2cc"}} />
        {{/if}}
        <span class="pl-2" data-test-step-label>{{@step.label}}</span>
      </div>
      {{#if @step.substeps}}
        <div class="fade-top fade-bottom">
          <ol
            class="list-none text-sm pv-0 pl-6 pr-0 mb-1 overflow-y-scroll"
            style="max-height: 6.25rem"
            data-test-substeps-list
          >
            {{#each @step.substeps as |substep index|}}
              <Substep
                @label={{substep.label}}
                @isActive={{eq index this.currentSubstep}}
                @isDone={{or this.isDone (lt index this.currentSubstep)}}
              />
            {{/each}}
          </ol>
        </div>
      {{/if}}
    </li>
  </template>
}
